/* ==========================================================================
   nordic.js — behaviour for the ng-* section pack.
   Self-contained, no dependencies, safe to load once per page.

   Lesson carried over from the Sunday Golf build: a control that silently
   does nothing is worse than one that errors. Every binding below either
   finds its target or warns to the console, so a broken wire is visible.
   ========================================================================== */
(() => {
  'use strict';

  const warn = (msg, el) => console.warn('[nordic] ' + msg, el || '');

  /* ---------------------------------------------------------------------
     Carousels. Arrows live OUTSIDE the track (this is exactly where the
     Sunday build broke — it looked for controls inside the scroller), so
     they are resolved from a shared [data-ng-carousel] ancestor.
     --------------------------------------------------------------------- */
  function initCarousel(root) {
    const track = root.querySelector('[data-ng-track]');
    if (!track) return warn('carousel has no [data-ng-track]', root);

    const prev = root.querySelector('[data-ng-prev]');
    const next = root.querySelector('[data-ng-next]');
    if (!prev || !next) warn('carousel is missing an arrow', root);

    const step = () => {
      const card = track.querySelector('.ng-card, .ng-marquee__item');
      const gap = parseFloat(getComputedStyle(track).gap) || 20;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= max;
    };

    // nordic.js is the single owner of scroll smoothness. The stylesheet must
    // NOT also set `scroll-behavior: smooth` on .ng-track — when both are set,
    // the CSS animation swallows the programmatic scroll and scrollLeft never
    // changes, so the arrows appear bound but move nothing.
    const behavior = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: behavior() }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: behavior() }));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ---------------------------------------------------------------------
     Marquee.

     A single duplicate and a -50% keyframe is NOT enough. That only loops
     seamlessly when one set of phrases is already wider than the viewport.
     When it is narrower — four short phrases on a 1368px screen measured
     1078px, a 290px shortfall — the track runs out of content before the
     keyframe wraps and a blank band flashes at the right edge every cycle.

     So: measure one set, tell CSS to shift by exactly that distance, and
     clone until the track covers the viewport plus one whole set. The shift
     is a px value rather than -50% precisely so the clone count no longer
     has to be exactly two.

     Idempotent — it strips its own clones first, so it is safe to re-run on
     resize and on a theme-editor section reload.
     --------------------------------------------------------------------- */
  function initMarquee(track) {
    [...track.children].forEach((node) => {
      if (node.dataset.ngClone === 'true') node.remove();
    });

    const originals = [...track.children];
    if (!originals.length) return warn('marquee track is empty', track);

    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const last = originals[originals.length - 1];
    // offsetLeft is immune to the running transform, so this measures true.
    const setWidth = last.offsetLeft + last.offsetWidth - originals[0].offsetLeft + gap;
    if (!setWidth) return warn('marquee set has no width', track);

    track.style.setProperty('--ng-marquee-shift', '-' + setWidth + 'px');

    const viewport = track.parentElement ? track.parentElement.clientWidth : 0;
    const copies = Math.max(1, Math.ceil((viewport + setWidth) / setWidth));
    for (let i = 0; i < copies; i += 1) {
      originals.forEach((node) => {
        const copy = node.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.dataset.ngClone = 'true';
        track.appendChild(copy);
      });
    }
  }

  /* ---------------------------------------------------------------------
     Countdown. Reads an ISO deadline off the element. If the attribute is
     absent or unparseable the element is left exactly as authored rather
     than being filled with an invented number.

     A [data-ng-d] slot is OPTIONAL. When present the remainder is split into
     days + hours-of-day; when absent the hours slot carries the total hours,
     which is what ng-announcement has always done. A month-long deadline
     reading "743h" is technically true and useless, hence the days slot.

     At expiry: if the element carries a non-empty data-ng-expired string the
     digits are replaced by it, otherwise the element hides. Either way the
     timer stops — a countdown that ticks into negative numbers is worse than
     no countdown.
     --------------------------------------------------------------------- */
  function initCountdown(el) {
    const raw = el.getAttribute('data-ng-deadline');
    if (!raw) return warn('countdown has no data-ng-deadline', el);
    const target = Date.parse(raw);
    if (Number.isNaN(target)) return warn('countdown deadline is unparseable: ' + raw, el);

    const slots = {
      d: el.querySelector('[data-ng-d]'),
      h: el.querySelector('[data-ng-h]'),
      m: el.querySelector('[data-ng-m]'),
      s: el.querySelector('[data-ng-s]')
    };
    if (!slots.h || !slots.m || !slots.s) return warn('countdown is missing a digit slot', el);

    const expired = (el.getAttribute('data-ng-expired') || '').trim();

    const pad = (n) => String(n).padStart(2, '0');

    // `timer` MUST be declared with let, above tick(), and initialised to null.
    // The first tick() runs synchronously, before setInterval has returned. If
    // the deadline is already in the past that first call reaches the expiry
    // branch immediately — and with `const timer = setInterval(...)` sitting
    // below, clearInterval(timer) throws a ReferenceError out of the temporal
    // dead zone. The whole handler then dies silently and the bar is left
    // showing dashes forever. That is the common case, not the rare one: it is
    // every page load after the deadline passes.
    let timer = null;

    const finish = () => {
      if (timer) clearInterval(timer);
      el.dataset.ngDone = 'true';   // stops the post-tick from starting an interval
      if (!expired) { el.hidden = true; return; }
      const digits = el.querySelector('.ng-count');
      if (digits) {
        digits.textContent = expired;
        digits.removeAttribute('aria-hidden');
      } else {
        el.textContent = expired;
      }
    };

    const tick = () => {
      const left = Math.floor((target - Date.now()) / 1000);
      if (left <= 0) return finish();
      if (slots.d) {
        slots.d.textContent = pad(Math.floor(left / 86400));
        slots.h.textContent = pad(Math.floor((left % 86400) / 3600));
      } else {
        slots.h.textContent = pad(Math.floor(left / 3600));
      }
      slots.m.textContent = pad(Math.floor((left % 3600) / 60));
      slots.s.textContent = pad(left % 60);
    };

    tick();
    if (!el.hidden && !el.dataset.ngDone) timer = setInterval(tick, 1000);
  }

  /* ---------------------------------------------------------------------
     Background video. Autoplay is a motion effect, so under reduced-motion
     the video is paused and its poster is left showing rather than looping
     behind the headline. Also pauses when scrolled out of view — a hero video
     playing to nobody is pure battery and bandwidth.
     --------------------------------------------------------------------- */
  function initBgVideo(wrap) {
    const video = wrap.querySelector('video');
    if (!video) return warn('bg video wrapper has no <video>', wrap);

    video.muted = true;              // belt and braces for iOS autoplay
    video.setAttribute('playsinline', '');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    // iOS Low Power Mode (and some data savers) reject autoplay but allow
    // play() from a user gesture. Without this retry the hero sits frozen on
    // its poster for the whole visit even though playback is permitted.
    let retryArmed = false;
    const armGestureRetry = () => {
      if (retryArmed) return;
      retryArmed = true;
      const retry = () => {
        if (reduced.matches) return;
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      };
      ['touchend', 'click'].forEach((t) =>
        document.addEventListener(t, retry, { once: true, passive: true }));
    };

    const apply = () => {
      if (reduced.matches) { video.pause(); return; }
      const p = video.play();
      if (p && p.catch) p.catch(armGestureRetry); // refused: poster stands in until first tap
    };
    apply();
    reduced.addEventListener('change', apply);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (reduced.matches) return;
          if (e.isIntersecting) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
          else video.pause();
        });
      }, { threshold: 0.15 }).observe(video);
    }
  }

  /* ---------------------------------------------------------------------
     Anchor jumps. Horizon puts overflow:hidden on html and body and scrolls
     div.page-wrapper instead, so native fragment navigation scrolls a
     scroller that never moves — a plain href="#ng-offer" visibly does
     nothing (verified live 2026-08-09). Every in-page anchor therefore
     gets an explicit scrollIntoView, which walks nested scroll containers
     correctly. Cross-page fragments ("/#ng-offer" from the promo bar on
     another page) are handled by the load-time branch.
     --------------------------------------------------------------------- */
  function initAnchorJumps() {
    // Smooth scrolling is deliberately NOT used here. The wrapper carries
    // scroll-behavior: smooth from Horizon, and every smooth programmatic
    // scroll against it gets cancelled to 0 (verified live 2026-08-09, same
    // failure mode as the carousel note above). Instant + explicit position
    // math is what actually moves it.
    const jump = (id) => {
      const target = document.getElementById(id);
      if (!target) return warn('anchor target #' + id + ' not found');
      const wrap = document.querySelector('.page-wrapper');
      if (wrap && wrap.scrollHeight > wrap.clientHeight) {
        const y = target.getBoundingClientRect().top - wrap.getBoundingClientRect().top + wrap.scrollTop;
        wrap.style.scrollBehavior = 'auto';
        wrap.scrollTop = y;
      } else {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    };

    document.querySelectorAll('a[href*="#"]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      const hashAt = href.indexOf('#');
      const path = href.slice(0, hashAt);
      const id = href.slice(hashAt + 1);
      if (!id) return;
      // Only intercept when the link stays on this page.
      if (path && path !== window.location.pathname && path !== '/') return;
      if (path === '/' && window.location.pathname !== '/') return;
      a.addEventListener('click', (e) => {
        if (!document.getElementById(id)) return; // let it navigate
        e.preventDefault();
        history.replaceState(null, '', '#' + id);
        jump(id);
      });
    });

    if (window.location.hash.length > 1) {
      // Layout is still settling at DOMContentLoaded (hero video sizing);
      // jumping immediately lands short.
      setTimeout(() => jump(window.location.hash.slice(1)), 150);
    }
  }

  /* --------------------------------------------------------------------- */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-ng-carousel]').forEach(initCarousel);
    (scope || document).querySelectorAll('[data-ng-marquee]').forEach(initMarquee);
    (scope || document).querySelectorAll('[data-ng-countdown]').forEach(initCountdown);
    (scope || document).querySelectorAll('[data-ng-bg-video]').forEach(initBgVideo);
    if (!scope) initAnchorJumps();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Re-bind when the Shopify theme editor swaps a section in.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));

  // A widening window can outrun the clone count, which is what puts the
  // blank band back. Re-measure after the resize settles.
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.querySelectorAll('[data-ng-marquee]').forEach(initMarquee);
    }, 200);
  });
})();
